import { z } from 'zod';
import { useInfiniteQuery } from '@tanstack/react-query';
import type { InfiniteData } from '@tanstack/react-query';
import { apiGetValidated } from '../api/client';

type UsePaginatedResult<T> = {
  readonly items: ReadonlyArray<T>;
  readonly hasMore: boolean;
  readonly loadMore: () => void;
  readonly isLoading: boolean;
  readonly isFetchingNextPage: boolean;
  readonly error: Error | null;
};

const usePaginatedList = <T>(config: {
  readonly queryKey: ReadonlyArray<string>;
  readonly baseUrl: string;
  readonly itemsKey: string;
  readonly itemSchema: z.ZodType<T>;
  readonly limit?: number;
  readonly enabled?: boolean;
}): UsePaginatedResult<T> => {
  const { queryKey, baseUrl, itemsKey, itemSchema, limit = 20, enabled = true } = config;

  const responseSchema = z.looseObject({
    [itemsKey]: z.array(itemSchema),
    cursor: z.string().optional(),
  });

  type PageResponse = z.infer<typeof responseSchema>;

  const query = useInfiniteQuery<PageResponse, Error, InfiniteData<PageResponse>, ReadonlyArray<string>, string>({
    queryKey: [...queryKey],
    queryFn: ({ pageParam }) => {
      const separator = baseUrl.includes('?') ? '&' : '?';
      const params = pageParam
        ? `${separator}limit=${limit}&cursor=${pageParam}`
        : `${separator}limit=${limit}`;
      return apiGetValidated(`${baseUrl}${params}`, responseSchema);
    },
    initialPageParam: '',
    getNextPageParam: (lastPage): string | undefined => {
      const c = lastPage.cursor;
      return typeof c === 'string' ? c : undefined;
    },
    enabled,
  });

  const items: ReadonlyArray<T> = query.data?.pages.flatMap(
    (page) => (page[itemsKey] as ReadonlyArray<T>) ?? [],
  ) ?? [];

  return {
    items,
    hasMore: query.hasNextPage ?? false,
    loadMore: () => { void query.fetchNextPage(); },
    isLoading: query.isLoading,
    isFetchingNextPage: query.isFetchingNextPage,
    error: query.error,
  };
};

export { usePaginatedList };
export type { UsePaginatedResult };
